import h from 'hyperscript';
import VectorSource from 'ol/source/Vector';
import GeoJSONFormat from 'ol/format/GeoJSON';
import { bbox as bboxLoadingStrategy } from 'ol/loadingstrategy';
import { intersects } from 'ol/extent';
import { unByKey } from 'ol/Observable';
import { LayerAttachmentsApi as AttachmentsApi } from 'cadenza/api-client/layer-attachments-api';
import { logger } from 'cadenza/utils/logging';
import { loadImage } from 'cadenza/utils/load-image';
import { easeOutQuad } from 'cadenza/utils/easing-util';

import { PhotoLayerFeatureLayout,
  DEFAULT_IMAGE_SIZE,
  EXPLODED_IMAGE_SIZE
} from './photo-layer-feature-layout';
import { PhotoLayerFeatureProperty as FeatureProperty } from './photo-layer-feature-property';
import { defaultStyle } from './photo-layer-style';
import { extentAt, featureSortFunction, photoFeatureLocation } from './photo-layer-utils';

const animationDuration = 300;
const shadowSupported = 'filter' in h('canvas').getContext('2d');

const MISSING = '/images/missing.png';

export class PhotoLayerSource extends VectorSource {

  constructor ({ map, masterLayerId }) {
    const attachmentsApi = new AttachmentsApi(masterLayerId);

    super({
      format: new GeoJSONFormat(),

      loader (extent) {
        attachmentsApi.getFeaturesWithAttachments(
          extent,
          map.getSize(),
          map.getView().getProjection().getCode(),
          'image/*')
          .then((res) => {
            this.refresh();
            this.loadedExtentsRtree_.insert(extent, { extent }); // prevent endless loading
            const features = this.getFormat().readFeatures(res).sort(featureSortFunction);
            processFeatures(features, this, map, this._savedLayouts);
            return features;
          })
          .then(loadThumbnails)
          .then(features => {
            this.addFeatures(features);
            this._animateExplodedFeatures(features, map);
          })
          .catch((error) => {
            logger.error('Failed to get features with attachments', error);
            this.removeLoadedExtent(extent);
          });
      },

      strategy: bboxLoadingStrategy
    });

    this._savedLayouts = new Map();
    this._map = map;
    this.set('masterlayer', masterLayerId);
  }

  loadFeatures (extent, resolution, projection) {
    if (resolution !== this._previousResolution) {
      this._previousResolution = resolution;
      this._savedLayouts.clear();

      /*
       * We do server-side clustering based on the resolution,
       * so we need to load this extent although it might be
       * already in the loadedExtentsRtree_.
       *
       * clear() is the only *public* API to reset the loadedExtentsRtree_,
       * but this also clears the features, which would cause flickering
       * (old features are removed before the new ones are loaded).
       * That's why we do it in the loader immediately before the newly
       * loaded features are added.
       */
      this.loader_(extent, resolution, projection);
    } else {
      this._saveCustomLayouts();
      super.loadFeatures(extent, resolution, projection);
    }
  }

  getSortedFeatures () {
    return this.getFeatures().slice().sort(featureSortFunction);
  }

  reprocessFeatures () {
    this._stopAnimation();
    this._saveCustomLayouts();
    const previousExploded = this.exploded || {};
    const features = this.getSortedFeatures();
    features.forEach(feature => {
      // unset previous layout and animation progress (silently to avoid map redraw on each change)
      feature.set(FeatureProperty.LAYOUT, undefined, true);
      feature.set(FeatureProperty.ANIMATION_PROGRESS, undefined, true);
    });
    processFeatures(features, this, this._map, this._savedLayouts);
    loadThumbnails(features)
      .then(() => {
        features
          .filter(feature => this.exploded[feature.getId()] !== previousExploded[feature.getId()])
          .forEach(feature => feature.changed());
        this._animateExplodedFeatures(features, this._map);
      })
      .catch(() => logger.warn('failed to load photo layer attachment thumbnails'));
  }

  _saveCustomLayouts () {
    this.getFeatures().forEach(feature => {
      const layout = feature.get(FeatureProperty.LAYOUT);
      if (layout && !layout.isDefault) {
        this._savedLayouts.set(feature.getId(), layout);
      }
    });
  }

  serializeDisplacements () {
    return this.getFeatures().flatMap(feature => {
      const layout = feature.get(FeatureProperty.LAYOUT);
      if (!(layout && !layout.isDefault)) {
        return [];
      }

      const location = photoFeatureLocation(feature);
      const featurePixel = this._map.getPixelFromCoordinate(location);

      return layout.getDisplacements().flatMap(({ from, to }) => {
        const fromCoordinate = this._map.getCoordinateFromPixel(add(featurePixel, from));
        const toCoordinate = this._map.getCoordinateFromPixel(add(featurePixel, to));
        return [ ...fromCoordinate, ...toCoordinate ];
      });
    });
  }

  _animateExplodedFeatures (features, map) {
    const animatedFeatures = features.filter(feature => feature.get(FeatureProperty.ANIMATION_PROGRESS) !== undefined);

    this._stopAnimation();

    if (animatedFeatures.length) {
      const start = Date.now();

      this._animationListenerKey = map.on('postcompose', event => {
        const frameState = event.frameState;
        const elapsed = frameState.time - start;
        const animationProgress = easeOutQuad(Math.min(1, elapsed / animationDuration));

        animatedFeatures.forEach(feature => feature.set(FeatureProperty.ANIMATION_PROGRESS, animationProgress));

        if (elapsed > animationDuration) {
          unByKey(this._animationListenerKey);
          this._animationListenerKey = null;
        } else {
          map.render(); // tell OpenLayers to continue postcompose animation
        }
      });
    }
    return features;
  }

  _stopAnimation () {
    if (this._animationListenerKey) {
      unByKey(this._animationListenerKey);
      this._animationListenerKey = null;
    }
  }

}

function processFeatures (clusterFeatures, source, map, savedLayouts) {
  const envelopes = {};
  const centers = {};
  const exploded = {};
  const previousExploded = source.exploded || {};

  // This algorithm decides, which features can be displayed in exploded mode and which don't have enough place and should be collapsed.
  // At first it is assumed that all features are collapsed. A list of minimal envelopes necessary for the collapsed style is created.
  // Only the features, for which a non-default layout existed previously (it is the user has moved the photos manually), stay exploded.
  // Then we go through all single features (clustered features are always collapsed) and calculate feature envelopes,
  // one for each displayed photo in the exploded style and one small for the center. If these envelopes do not overlap
  // with all other envelopes in the list, then the feature is marked to be displayed in exploded view and
  // feature minimal envelope is replaced with feature exploded style envelope list.
  clusterFeatures.forEach((feature) => {
    const location = photoFeatureLocation(feature);

    if (!feature.getId()) {
      feature.setId(location.join());
    }

    const featureId = feature.getId();
    const px = map.getPixelFromCoordinate(location);
    centers[featureId] = px;
    envelopes[featureId] = savedLayouts.has(featureId)
      ? savedLayouts.get(featureId).getAllEnvelopesAt(px)
      : [ extentAt(px, DEFAULT_IMAGE_SIZE) ];
  });

  clusterFeatures.forEach((clusterFeature) => {
    if (clusterFeature.get(FeatureProperty.CLUSTER_SIZE) === 1) { // check exploded view
      const featureId = clusterFeature.getId();
      const center = centers[featureId];
      const layout = savedLayouts.has(featureId) ? savedLayouts.get(featureId) : new PhotoLayerFeatureLayout(clusterFeature);
      const candidateEnvelopes = layout.getAllEnvelopesAt(center);
      const featureExploded = !layout.isDefault || enoughSpace(featureId, candidateEnvelopes);

      exploded[featureId] = featureExploded;
      if (featureExploded) {
        clusterFeature.set(FeatureProperty.LAYOUT, layout, true);
        envelopes[featureId] = candidateEnvelopes;

        if (previousExploded[featureId] === false) {
          clusterFeature.set(FeatureProperty.ANIMATION_PROGRESS, 0, true);
        }
      }
    }
  });

  source.exploded = exploded;

  function enoughSpace (featureId, candidateEnvelopes) {
    return Object.entries(envelopes)
      .flatMap(([ id, featureEnvelopes ]) => id === featureId ? [] : featureEnvelopes)
      .every(envelope => candidateEnvelopes.every(candidateEnvelope => !intersects(envelope, candidateEnvelope)));
  }
}

function loadThumbnails (features) {
  const images = features.reduce((images, feature) => {
    const layout = feature.get(FeatureProperty.LAYOUT);
    const attachments = feature.get(FeatureProperty.ATTACHMENTS);
    if (layout) {
      const attachmentsToLoad = attachments.slice(0, layout.length);
      return images.concat(attachmentsToLoad.map(loadThumbnail));
    } else {
      return images.concat(loadThumbnail(attachments[0]));
    }
  }, []);
  return Promise.all(images).then(() => features);
}

function loadThumbnail (attachment) {
  if (attachment.thumbnail) {
    return Promise.resolve();
  }

  return loadImage({ src: attachment.thumbnailUrl }).then(img => {
    // Thumbnail shadows are created in advance, because otherwise it takes
    // too much time to draw them with a blur filter on canvas during animation.
    if (shadowSupported) {
      attachment.shadowRendered = true;
      return createImageWithShadow(img,
        EXPLODED_IMAGE_SIZE,
        defaultStyle.shadowBlur,
        defaultStyle.shadowOffset);
    } else {
      return img;
    }
  }).catch(() => {
    logger.warn('failed to load photo layer attachment thumbnail', attachment.thumbnailUrl);
    return loadImage({ src: window.Disy.themePath + MISSING });
  }).then(img => {
    attachment.thumbnail = img;
  });
}

const shadowCanvas = h('canvas', {
  width: EXPLODED_IMAGE_SIZE * 3,
  height: EXPLODED_IMAGE_SIZE * 3
});
const shadowContext = shadowCanvas.getContext('2d');

function createImageWithShadow (image, imageSize, blurRadius, shadowOffset) {
  shadowContext.clearRect(0, 0, shadowCanvas.width, shadowCanvas.height);
  shadowContext.save();
  shadowContext.filter = `blur(${blurRadius}px)`;
  shadowContext.drawImage(image, imageSize + shadowOffset, imageSize + shadowOffset, imageSize, imageSize);
  shadowContext.restore();
  shadowContext.drawImage(image, imageSize, imageSize, imageSize, imageSize);
  // export it to an image instead of returning canvas
  // element because of rendering performance reasons
  return loadImage({ src: shadowCanvas.toDataURL() });
}

function add (point1, point2) {
  return [ point1[0] + point2[0], point1[1] + point2[1] ];
}
